import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock, Car, Loader2, LogIn } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useAuth, startGoogleLogin } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  new: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  confirmed: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
};

export default function MyBookingsPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const handleBook = () => navigate("/#booking");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/user/bookings`, { withCredentials: true })
      .then((r) => setBookings(r.data))
      .catch(() => setBookings([]))
      .finally(() => setLoading(false));
  }, [authLoading, user]);

  return (
    <div className="dark grain bg-[#050505] min-h-screen" data-testid="mybookings-page">
      <Navbar onBook={handleBook} />

      <section className="relative pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <button
            data-testid="mybookings-back"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors mb-10"
          >
            <ArrowLeft size={14} /> Back
          </button>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-heading text-5xl sm:text-6xl font-extrabold tracking-tighter text-white leading-[0.95]"
          >
            My Bookings
          </motion.h1>
          <p className="mt-4 text-zinc-400 max-w-xl">
            {user ? `Signed in as ${user.name || user.email}. Here are your past and upcoming appointments.` : "Sign in to see your bookings history in one place."}
          </p>

          <div className="mt-12">
            {authLoading || loading ? (
              <div className="flex items-center gap-2 text-zinc-500 py-16 justify-center">
                <Loader2 className="animate-spin" size={20} /> Loading...
              </div>
            ) : !user ? (
              <div className="rounded-3xl border border-white/10 bg-[#111111] p-10 md:p-14 text-center">
                <p className="font-heading text-2xl font-semibold text-white">You're not signed in</p>
                <p className="mt-3 text-zinc-400 max-w-md mx-auto">
                  Sign in with Google to view your booking history and reference numbers.
                </p>
                <button
                  data-testid="mybookings-signin"
                  onClick={() => startGoogleLogin("/my-bookings")}
                  className="btn-3d mt-8 inline-flex items-center gap-2 rounded-full bg-[#fafafa] text-[#050505] font-semibold px-7 py-3.5 hover:bg-white"
                >
                  <LogIn size={16} /> Sign in with Google
                </button>
              </div>
            ) : bookings.length === 0 ? (
              <div className="rounded-3xl border border-white/10 bg-[#111111] p-10 md:p-14 text-center">
                <p className="font-heading text-2xl font-semibold text-white">No bookings yet</p>
                <p className="mt-3 text-zinc-400 max-w-md mx-auto">
                  Book your first appointment and it'll appear here.
                </p>
                <button
                  data-testid="mybookings-book"
                  onClick={handleBook}
                  className="btn-3d mt-8 inline-flex items-center gap-2 rounded-full bg-[#fafafa] text-[#050505] font-semibold px-7 py-3.5 hover:bg-white"
                >
                  Book Your Detail <ArrowRight size={16} />
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map((b, i) => (
                  <motion.div
                    key={b.id}
                    data-testid={`mybooking-${b.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                    className="rounded-2xl border border-white/10 bg-[#111111] p-6 md:p-7"
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-heading text-lg font-semibold text-white">{b.service}</h3>
                          <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium capitalize ${STATUS_STYLES[b.status] || ""}`}>
                            {b.status}
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-400">
                          <span className="flex items-center gap-2"><Calendar size={14} /> {b.preferred_date}</span>
                          <span className="flex items-center gap-2"><Clock size={14} /> {b.preferred_time}</span>
                          {(b.vehicle_year || b.vehicle_make || b.vehicle_model) && (
                            <span className="flex items-center gap-2"><Car size={14} /> {[b.vehicle_year, b.vehicle_make, b.vehicle_model].filter(Boolean).join(" ")}</span>
                          )}
                        </div>
                        {b.notes && (
                          <p className="mt-3 text-sm text-zinc-500 italic border-l-2 border-white/10 pl-3">{b.notes}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Confirmation</p>
                        <p className="font-heading text-lg font-bold text-white mt-1">{b.confirmation_number}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
